import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Avatar,
  Snackbar,
  Alert,
  Dialog,
  Select,
  FormControl,
  InputLabel,
  alpha,
  useTheme,
  Stack,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack,
  MoreVert,
  Send,
  Image as ImageIcon,
  InsertDriveFile,
  ConfirmationNumberOutlined,
  CalendarToday,
  PersonOutline,
  EmailOutlined,
  AttachFile,
  Update,
} from '@mui/icons-material';
import { apiService } from '../services/api.service';
import { Ticket, TicketReply, TicketStatus } from '../types';
import Layout from '../components/Layout';

const TicketDetail: React.FC = () => {
  const theme = useTheme();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [replies, setReplies] = useState<TicketReply[]>([]);
  const [loading, setLoading] = useState(true);
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState<TicketStatus>('open');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  const loadTicket = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const ticketData = await apiService.getTicket(id);
      setTicket(ticketData);
      setNewStatus(ticketData.status);
    } catch (error) {
      console.error('Failed to load ticket:', error);
      setSnackbarMessage('Failed to load ticket');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadReplies = useCallback(async () => {
    if (!id) return;
    try {
      const repliesData = await apiService.getTicketReplies(id);
      setReplies(repliesData);
    } catch (error) {
      console.error('Failed to load replies:', error);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      loadTicket();
      loadReplies();
    }
  }, [id, loadTicket, loadReplies]);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const handleStatusChange = () => { setStatusDialogOpen(true); handleMenuClose(); };

  const handleUpdateStatus = async () => {
    if (!id) return;
    try {
      await apiService.updateTicketStatus(id, newStatus);
      await loadTicket();
      setStatusDialogOpen(false);
      setSnackbarMessage('Ticket status updated successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      console.error(error);
      setSnackbarMessage('Failed to update ticket status');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleSendReply = async () => {
    if (!id || !replyText.trim()) return;
    setSendingReply(true);
    try {
      await apiService.addTicketReply({
        ticketId: id,
        senderId: 'admin', // In a real app, this would be the logged in admin ID
        senderName: 'Admin Support',
        message: replyText.trim(),
        timestamp: new Date().toISOString(),
        isAdmin: true,
      });
      setReplyText('');
      await loadReplies();
      if (ticket && ticket.status === 'open') {
        await apiService.updateTicketStatus(id, 'inProgress');
        await loadTicket();
      }
      setSnackbarMessage('Reply sent successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      console.error(error);
      setSnackbarMessage('Failed to send reply');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setSendingReply(false);
    }
  };


  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <Layout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress size={32} thickness={5} />
        </Box>
      </Layout>
    );
  }

  if (!ticket) {
    return (
      <Layout>
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="error" sx={{ fontWeight: 800 }}>Reference ticket not found</Typography>
          <Button startIcon={<ArrowBack />} onClick={() => navigate('/tickets')} sx={{ mt: 2, borderRadius: 2 }}>Back to Tickets</Button>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          mb: 4,
          gap: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', width: { xs: '100%', sm: 'auto' } }}>
            <IconButton onClick={() => navigate('/tickets')} sx={{ mr: 2, bgcolor: alpha(theme.palette.divider, 0.1) }}>
              <ArrowBack fontSize="small" />
            </IconButton>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: -1, fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>Ticket Review</Typography>
              <Typography color="text.secondary" variant="body2">Managing conversation for <strong>#{ticket.ticketNumber}</strong></Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', ml: { xs: 0, sm: 'auto' }, width: { xs: '100%', sm: 'auto' }, justifyContent: { xs: 'space-between', sm: 'flex-end' } }}>
            <Chip
              label={ticket.status}
              sx={{
                fontWeight: 800,
                textTransform: 'uppercase',
                height: 32,
                px: 1,
                bgcolor: ticket.status === 'resolved'
                  ? alpha(theme.palette.success.main, 0.15)
                  : ticket.status === 'open'
                    ? alpha(theme.palette.warning.main, 0.15)
                    : ticket.status === 'inProgress'
                      ? alpha(theme.palette.info.main, 0.15)
                      : alpha(theme.palette.grey[500], 0.15),
                color: ticket.status === 'resolved'
                  ? 'success.main'
                  : ticket.status === 'open'
                    ? 'warning.main'
                    : ticket.status === 'inProgress'
                      ? 'info.main'
                      : 'text.secondary',
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: ticket.status === 'resolved'
                  ? alpha(theme.palette.success.main, 0.2)
                  : ticket.status === 'open'
                    ? alpha(theme.palette.warning.main, 0.2)
                    : ticket.status === 'inProgress'
                      ? alpha(theme.palette.info.main, 0.2)
                      : alpha(theme.palette.grey[500], 0.2)
              }}
            />
            <IconButton onClick={handleMenuClick} sx={{ ml: 2, bgcolor: alpha(theme.palette.divider, 0.1) }}>
              <MoreVert fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 340px' }, gap: 4 }}>
          {/* Main Chat/Conversation Area */}
          <Box>
            <Paper sx={{ p: 4, mb: 4, borderRadius: 4, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="h5" sx={{ fontWeight: 800, mb: 1.5 }}>{ticket.subject}</Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{ticket.description}</Typography>

              {ticket.attachments && ticket.attachments.length > 0 && (
                <Box sx={{ mt: 3, pt: 3, borderTop: '1px dashed', borderColor: 'divider' }}>
                  <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', display: 'block', mb: 1.5 }}>Initial Attachments</Typography>
                  <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', gap: 2 }}>
                    {ticket.attachments.map((at, idx) => (
                      <Paper key={idx} sx={{ p: 1.5, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1.5, border: '1px solid', borderColor: 'divider', minWidth: 200 }}>
                        {at.fileType.includes('image') ? <ImageIcon color="primary" /> : <InsertDriveFile color="action" />}
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Typography variant="caption" noWrap sx={{ fontWeight: 700, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}>{at.fileName}</Typography>
                        </Box>
                        <Button size="small" href={at.fileUrl} target="_blank" sx={{ fontWeight: 700 }}>Open</Button>
                      </Paper>
                    ))}
                  </Stack>
                </Box>
              )}
            </Paper>

            <Typography variant="h6" sx={{ fontWeight: 800, mb: 2, px: 1 }}>Conversation log ({replies.length})</Typography>

            <Stack spacing={2} sx={{ mb: 4 }}>
              {replies.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center', bgcolor: alpha(theme.palette.divider, 0.05), border: '1px dashed', borderColor: 'divider' }}>
                  <Typography color="text.secondary" variant="body2">No replies recorded yet. Start the conversation below.</Typography>
                </Paper>
              ) : (
                replies.map((reply) => (
                  <Box key={reply.id} sx={{ display: 'flex', justifyContent: reply.isAdmin ? 'flex-end' : 'flex-start' }}>
                    <Box sx={{ maxWidth: { xs: '95%', sm: '85%' }, display: 'flex', gap: 1.5, flexDirection: reply.isAdmin ? 'row-reverse' : 'row' }}>
                      <Avatar sx={{ width: 32, height: 32, mt: 0.5, bgcolor: reply.isAdmin ? 'primary.main' : 'secondary.main', fontSize: '0.75rem', fontWeight: 700 }}>{reply.senderName.charAt(0)}</Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Paper sx={{
                          p: 2,
                          borderRadius: 3,
                          bgcolor: reply.isAdmin ? 'primary.main' : 'background.paper',
                          color: reply.isAdmin ? 'primary.contrastText' : 'text.primary',
                          boxShadow: theme.shadows[1],
                          border: '1px solid',
                          borderColor: reply.isAdmin ? 'primary.main' : 'divider'
                        }}>
                          <Typography variant="body2" sx={{ lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>{reply.message}</Typography>
                        </Paper>
                        <Typography variant="caption" sx={{ mt: 0.5, display: 'block', px: 1, textAlign: reply.isAdmin ? 'right' : 'left', color: 'text.secondary', fontWeight: 600 }}>
                          {reply.senderName} • {formatTime(reply.timestamp)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                ))
              )}
            </Stack>

            <Paper sx={{ p: 1, borderRadius: 4, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
              <TextField
                fullWidth multiline rows={4}
                placeholder="Compose your reply..."
                variant="standard"
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                sx={{ px: 2, py: 1, '& .MuiInput-underline:before, & .MuiInput-underline:after': { display: 'none' } }}
              />
              <Box sx={{ p: 1, bgcolor: alpha(theme.palette.divider, 0.05), display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderRadius: '0 0 16px 16px' }}>
                <IconButton size="small"><AttachFile fontSize="small" /></IconButton>
                <Button
                  onClick={handleSendReply}
                  disabled={!replyText.trim() || sendingReply}
                  variant="contained" size="small" endIcon={sendingReply ? <CircularProgress size={16} color="inherit" /> : <Send />}
                  sx={{ borderRadius: 2, fontWeight: 700, px: 3 }}
                >
                  Send Reply
                </Button>
              </Box>
            </Paper>
          </Box>

          {/* Sidebar / Metadata Area */}
          <Box>
            <Stack spacing={3}>
              <Paper sx={{ p: 3, borderRadius: 4, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1, display: 'block', mb: 3 }}>User Information</Typography>
                <Stack spacing={3}>
                  <Box>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      <PersonOutline sx={{ fontSize: 16, color: 'primary.main' }} />
                      <Typography variant="caption" sx={{ fontWeight: 800 }}>Primary Contact</Typography>
                    </Stack>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{ticket.userName}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><EmailOutlined sx={{ fontSize: 12 }} /> {ticket.userEmail}</Typography>
                  </Box>
                  <Divider />
                  <Box>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      <ConfirmationNumberOutlined sx={{ fontSize: 16, color: 'secondary.main' }} />
                      <Typography variant="caption" sx={{ fontWeight: 800 }}>ID References</Typography>
                    </Stack>
                    <Box sx={{ bgcolor: alpha(theme.palette.divider, 0.05), p: 1.5, borderRadius: 2 }}>
                      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>Ticket ID</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 700, fontFamily: 'monospace' }}>{ticket.id}</Typography>
                      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 1 }}>User ID</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 700, fontFamily: 'monospace' }}>{ticket.userId}</Typography>
                    </Box>
                  </Box>
                </Stack>
              </Paper>

              <Paper sx={{ p: 3, borderRadius: 4, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1, display: 'block', mb: 3 }}>System Timelines</Typography>
                <Stack spacing={2.5}>
                  <Box>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <CalendarToday sx={{ fontSize: 16, opacity: 0.5 }} />
                      <Box>
                        <Typography variant="caption" sx={{ display: 'block', lineHeight: 1, color: 'text.secondary' }}>Created At</Typography>
                        <Typography variant="caption" sx={{ fontWeight: 700 }}>{formatDate(ticket.createdAt)}</Typography>
                      </Box>
                    </Stack>
                  </Box>
                  <Box>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Tooltip title="Last activity recorded"><Update sx={{ fontSize: 16, opacity: 0.5 }} /></Tooltip>
                      <Box>
                        <Typography variant="caption" sx={{ display: 'block', lineHeight: 1, color: 'text.secondary' }}>Last Updated</Typography>
                        <Typography variant="caption" sx={{ fontWeight: 700 }}>{formatDate(ticket.updatedAt)}</Typography>
                      </Box>
                    </Stack>
                  </Box>
                  {ticket.resolvedAt && (
                    <Box sx={{ p: 1.5, bgcolor: alpha(theme.palette.success.main, 0.1), borderRadius: 2, border: '1px solid', borderColor: alpha(theme.palette.success.main, 0.2) }}>
                      <Typography variant="caption" sx={{ display: 'block', color: 'success.main', fontWeight: 800 }}>RESOLVED ON</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 700 }}>{formatDate(ticket.resolvedAt)}</Typography>
                    </Box>
                  )}
                </Stack>
              </Paper>
            </Stack>
          </Box>
        </Box>

        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose} PaperProps={{ sx: { borderRadius: 3, minWidth: 180, mt: 1, boxShadow: theme.shadows[4] } }}>
          <MenuItem onClick={handleStatusChange} sx={{ py: 1.5, gap: 1.5 }}>
            <Update fontSize="small" color="action" />
            <Typography variant="body2" fontWeight={600}>Modify Status</Typography>
          </MenuItem>
        </Menu>

        <Dialog
          open={statusDialogOpen}
          onClose={() => setStatusDialogOpen(false)}
          PaperProps={{ sx: { borderRadius: 4, width: '100%', maxWidth: 400, m: 2 } }}
        >
          <Box sx={{ p: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>Update Ticket State</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>Transition the ticket status to manage user expectations.</Typography>

            <FormControl fullWidth sx={{ mb: 4 }}>
              <InputLabel>Target Status</InputLabel>
              <Select
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value as TicketStatus)}
                sx={{ borderRadius: 3 }}
              >
                <MenuItem value="open">Open (Awaiting Initial Action)</MenuItem>
                <MenuItem value="inProgress">In Progress (Active Work)</MenuItem>
                <MenuItem value="resolved">Resolved (Issue Fixed)</MenuItem>
                <MenuItem value="closed">Closed (Archive)</MenuItem>
              </Select>
            </FormControl>

            <Stack direction="row" spacing={2}>
              <Button fullWidth onClick={() => setStatusDialogOpen(false)} sx={{ fontWeight: 700 }}>Cancel</Button>
              <Button fullWidth variant="contained" onClick={handleUpdateStatus} sx={{ fontWeight: 700, borderRadius: 2 }}>Save Changes</Button>
            </Stack>
          </Box>
        </Dialog>

        <Snackbar open={snackbarOpen} autoHideDuration={3000} onClose={() => setSnackbarOpen(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
          <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ borderRadius: 3, fontWeight: 700 }}>{snackbarMessage}</Alert>
        </Snackbar>
      </Box>
    </Layout>
  );
};

export default TicketDetail;
